import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css, ClassNames, } from '@emotion/react';
import { Component, memo, } from 'react';
import LazyLoad from 'react-lazyload';
import styleUtils from '../../styles/styleUtils';
import theme from '../../styles/theme';
import windowMatchMedia from '../../utils/windowMatchMedia';
import { Popover, PopoverContent, PopoverManager, } from '../Popover/index';
import { SwatchPicker, Swatch, } from '../SwatchPicker/index';
import { TextLink } from '../TextLink/index';
import { LAZY_LOAD_TOP_OFFSET, CYCLE_SPEED, FIRST_IMAGE_CYCLE_SPEED, FAVORITE_SUGGESTION_DURATION, } from './constants';
import { CustomColorSwatch } from './CustomColorSwatch';
import { FavoriteIconButton } from './FavoriteIconButton';
const touchDeviceBreakpoint = theme.breakpoints.medium;
const baseStylesString = `
  box-sizing: border-box;
  display: flex;
  flex-flow: column nowrap;
  max-width: ${styleUtils.rem(240)};
  padding: ${theme.spacing.x2} ${theme.spacing.x4};
  border: ${styleUtils.rem(1)} solid transparent;

  &:hover {
    border: ${styleUtils.rem(1)} solid ${theme.colors.gray400};
  }

  @media (max-width: ${touchDeviceBreakpoint}) {
    border: none;
    max-width: ${styleUtils.rem(200)};

    &:hover {
      border: none;
    }
  }
`;
// repeat class name to prevent global styles of the page from having priority
// over the expected style of the component
const titleAndImageSectionStyles = css `
  && {
    position: relative;
    height: ${styleUtils.rem(240)};
    display: flex;
    text-decoration: none;
    color: ${theme.colors.gray700};
    flex: 0 0 auto;
    ${theme.media.lessThan(touchDeviceBreakpoint)(`
      height: ${styleUtils.rem(200)};
    `)}
  }
`;
// repeat class name to prevent global styles of the page from having priority
// over the expected style of the component
const titleAndImageStyles = (currentImage) => css `
  && {
    position: ${!currentImage && 'absolute'};
    height: ${!currentImage && '100%'};
    width: 100%;
    display: flex;
    flex-flow: column;
    align-items: center;
    opacity: ${currentImage ? 1 : 0};
    ${styleUtils.transition('opacity')}
  }
`;
// repeat class name to prevent global styles of the page from having priority
// over the expected style of the component
const hoverTitleStyles = (wrapperClassName) => css `
  && {
    ${theme.typeStyles.bodyExtraSmallStyles}
    text-align: center;
    white-space: pre-wrap;
    margin: 0 0 ${theme.spacing.x2};
    visibility: hidden;
  }

  .${wrapperClassName}:hover && {
    visibility: initial;
  }

  ${theme.media.lessThan(touchDeviceBreakpoint)(`
    display: none;
  `)}
`;
// repeat class name to prevent global styles of the page from having priority
// over the expected style of the component
const headerTextStyles = css `
  && {
    font-weight: ${theme.typeStyles.fontWeightBold};
  }
`;
// repeat class name to prevent global styles of the page from having priority
// over the expected style of the component
const imageContainerStyles = css `
  && {
    flex: 1 1 0;
    position: relative;
    width: 100%;
  }
`;
// repeat class name to prevent global styles of the page from having priority
// over the expected style of the component
// need to use absolute positioning because otherwise the img won't respect its
// container's height:
// https://stackoverflow.com/questions/14554908/imgs-max-height-not-respecting-parents-dimensions
const imageStyles = (withShadow) => css `
  && {
    position: absolute;
    bottom: 0;
    display: block;
    max-width: 100%;
    max-height: 100%;
    left: 50%;
    transform: translate(-50%);
    box-shadow: ${withShadow && `0 0 ${styleUtils.rem(1)} ${styleUtils.rem(2)} ${theme.colors.gray300}`};
  }
`;
const quicklookContainerStyles = css `
  align-self: stretch;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: ${theme.spacing.base};
  border-bottom: ${styleUtils.rem(1)} solid ${theme.colors.gray400};
  margin: ${theme.spacing.x4} 0 0;
  color: ${theme.colors.accent600};
  ${theme.media.lessThan(touchDeviceBreakpoint)(`
    display: none;
  `)}
`;
const tooltipPointerSize = 12;
const popoverStyles = css `
  margin-bottom: ${styleUtils.rem(tooltipPointerSize)};
  background: ${theme.colors.gray100};
  ${theme.media.lessThan(touchDeviceBreakpoint)(`
    display: none;
  `)}

  &::after {
    content: '';
    position: absolute;
    display: block;
    width: ${styleUtils.rem(tooltipPointerSize)};
    height: ${styleUtils.rem(tooltipPointerSize)};
    bottom: ${styleUtils.rem(tooltipPointerSize / 2)};
    left: calc(50% - ${styleUtils.rem(tooltipPointerSize / 2)});
    transform: rotate(45deg);
    background-color: ${theme.colors.gray100};
    box-shadow: ${styleUtils.rem(2)} ${styleUtils.rem(2)} ${styleUtils.rem(2.5)} 0 rgba(0, 0, 0, 0.2);
  }
`;
const favoriteSuggestionStyles = css `
  ${styleUtils.buttonReset}
  ${theme.typeStyles.bodySmallStyles}
  text-align: center;
  padding: ${theme.spacing.x3};
  margin: 0;

  &:hover {
    cursor: pointer;
  }
`;
const swatchPickerStyles = css `
  margin: ${theme.spacing.x4} 0 0;
`;
// repeat class name to prevent global styles of the page from having priority
// over the expected style of the component
const productNameStyles = css `
  && {
    ${theme.typeStyles.bodySmallStyles}
    ${theme.typeStyles.bodyBoldStyles}
    align-self: flex-start;
    max-width: 100%;
    text-decoration: none;
    color: ${theme.colors.gray700};
    margin: ${theme.spacing.x2} 0 0;
  }

  &&:hover {
    opacity: 0.8;
  }
`;
// repeat class name to prevent global styles of the page from having priority
// over the expected style of the component
const artistNameStyles = css `
  && {
    ${theme.typeStyles.bodyExtraSmallStyles}
    font-weight: 300;
    align-self: flex-start;
    max-width: 100%;
    text-decoration: none;
    color: ${theme.colors.gray700};
    overflow-wrap: break-word;
  }

  &&:hover {
    opacity: 0.8;
  }
`;
const merchandisingLineStyles = css `
  ${theme.typeStyles.bodyExtraSmallStyles}
  align-self: flex-start;
  max-width: 100%;
  color: #86504c;
  margin: ${theme.spacing.base} 0 0;
`;
const badgeImageStyles = css `
  height: ${styleUtils.rem(20)};
  margin: ${theme.spacing.x2} 0 0;
  align-self: flex-start;
  max-width: 100%;
`;
const orderSampleStyles = css `
  margin: ${theme.spacing.x2} 0 0;
  align-self: flex-start;
  max-width: 100%;
  text-align: left;
  color: ${theme.colors.accent600};
`;
const mobileFavoriteButtonStyles = css `
  flex: 0 0 auto;
  align-self: flex-start;
  margin: ${theme.spacing.x2} 0 0;
  ${theme.media.greaterThan(touchDeviceBreakpoint)(`
    display: none;
  `)}
`;
const colorwayWrapperStyles = css `
  display: flex;
  justify-content: space-between;
`;
const searchFavoriteIconStyles = css `
  color: ${theme.colors.accent600};
  margin-top: ${styleUtils.rem(18)}; /* hack to position favorites heart inline with swatch picker when there is and is not a swatch picker. */
  flex: 1;
  justify-content: flex-end;
  display: flex;

  &&:hover {
    color: ${theme.colors.accent800};
  }
`;
const actionTypes = {
    cycleImage: 'cycleImage',
    resetCycling: 'resetCycling',
    setFavoriteSuggestionOpen: 'setFavoriteSuggestionOpen',
    setTouchDeviceScreen: 'setTouchDeviceScreen',
    startCycling: 'startCycling',
};
const initialState = {
    cyclingImage: false,
    displayedImage: 0,
    favoriteSuggestionOpen: false,
    firstCycle: true,
    touchDeviceScreen: false,
};
const reducerFactory = (productImagesLength = 1) => (state, action) => {
    switch (action.type) {
        case actionTypes.startCycling:
            return Object.assign(Object.assign({}, state), { cyclingImage: true });
        case actionTypes.resetCycling:
            return Object.assign(Object.assign({}, state), { cyclingImage: false, displayedImage: 0, firstCycle: true });
        case actionTypes.cycleImage: {
            const { displayedImage = 0, } = state;
            return Object.assign(Object.assign({}, state), { displayedImage: (displayedImage + 1) % productImagesLength, firstCycle: false });
        }
        case actionTypes.setFavoriteSuggestionOpen:
            return Object.assign(Object.assign({}, state), { favoriteSuggestionOpen: action.value });
        case actionTypes.setTouchDeviceScreen:
            return Object.assign(Object.assign({}, state), { touchDeviceScreen: action.value });
        /* istanbul ignore next */
        default:
            throw new Error("Unexpected action dispatched to ProductCellPrimary's reducer");
    }
};
const defaultProps = {
    badgeUrl: undefined,
    className: '',
    colorwayColors: [],
    cycleOnHover: false,
    inlineFavoriteButton: false,
    merchandisingLine: undefined,
    onColorChange: () => { },
    onCustomColorClick: undefined,
    onCycle: () => { },
    onFavoriteClick: () => { },
    onOrderSample: () => { },
    onQuicklook: undefined,
    orderSampleText: undefined,
    quicklookLabel: 'Quicklook',
    shadowOnFirstImage: false,
    showColorSwatch: true,
    suggestFavorite: false,
};
class ProductCellPrimary extends Component {
    constructor() {
        super(...arguments);
        this.state = initialState;
        // if cycleOnHover is false or it's touch device, shorten the array to have only
        // the first element which will result in rendering only one image
        this.getProductImagesToRender = () => {
            var _a;
            const { cycleOnHover, productImages = [], } = this.props;
            const { touchDeviceScreen, } = this.state;
            return ((_a = ((!touchDeviceScreen
                && cycleOnHover)
                ? productImages
                : [
                    productImages[0],
                ])) === null || _a === void 0 ? void 0 : _a.filter(Boolean));
        };
        this.dispatch = (action) => {
            this.setState((state) => reducerFactory(this.getProductImagesToRender().length)(state, action));
        };
        this.handleFavoriteClick = () => {
            this.dispatch({
                type: actionTypes.setFavoriteSuggestionOpen,
                value: false,
            });
            const { onFavoriteClick = () => { }, } = this.props;
            onFavoriteClick();
        };
    }
    componentDidMount() {
        var _a;
        const { suggestFavorite, } = this.props;
        if (suggestFavorite) {
            this.dispatch({
                type: actionTypes.setFavoriteSuggestionOpen,
                value: true,
            });
            this.closeFavoriteSuggestionTimeoutId = setTimeout(() => this.dispatch({
                type: actionTypes.setFavoriteSuggestionOpen,
                value: false,
            }), FAVORITE_SUGGESTION_DURATION);
        }
        this.mediaQueryList = (window
            ? window.matchMedia(`(max-width: ${touchDeviceBreakpoint})`)
            : undefined);
        this.dispatch({
            type: actionTypes.setTouchDeviceScreen,
            value: (_a = this.mediaQueryList) === null || _a === void 0 ? void 0 : _a.matches,
        });
        this.mediaQueryListChangeHandler = ({ matches, }) => this.dispatch({
            type: actionTypes.setTouchDeviceScreen,
            value: matches,
        });
        windowMatchMedia.addListener(this.mediaQueryList, this.mediaQueryListChangeHandler);
    }
    componentDidUpdate(prevProps, prevState) {
        const { onCycle = () => { }, } = this.props;
        const { cyclingImage, firstCycle, } = this.state;
        if (cyclingImage !== prevState.cyclingImage ||
            firstCycle !== prevState.firstCycle ||
            onCycle !== prevProps.onCycle) {
            clearInterval(this.cycleIntervalId);
            if (cyclingImage) {
                this.cycleIntervalId = setInterval(() => {
                    this.dispatch({
                        type: actionTypes.cycleImage,
                    });
                    onCycle();
                }, 
                // first picture should have a shorter cycle speed to see a change
                firstCycle ? FIRST_IMAGE_CYCLE_SPEED : CYCLE_SPEED);
            }
        }
    }
    componentWillUnmount() {
        clearInterval(this.cycleIntervalId);
        clearTimeout(this.closeFavoriteSuggestionTimeoutId);
        if (this.mediaQueryList) {
            windowMatchMedia.removeListener(this.mediaQueryList, this.mediaQueryListChangeHandler);
        }
    }
    render() {
        const { artistName, artistUrl, badgeUrl, buyUrl, className, colorwayColors, cycleOnHover, favorited, inlineFavoriteButton, lazyloadImage, merchandisingLine, onColorChange, onCustomColorClick, onOrderSample, onQuicklook, orderSampleText, productName, quicklookLabel, selectedColor, shadowOnFirstImage, showColorSwatch, } = this.props;
        const productImagesToRender = this.getProductImagesToRender();
        const { displayedImage, favoriteSuggestionOpen, touchDeviceScreen, } = this.state;
        // When shape is default(just a rectangle) and when fif is on (shadowOnFirstImage = true),
        // with the white bg visually you cannot distinguish the card vs the page so add shadow
        // but only for the first image as we don't use scene7 img-s on the other cycler img-s.
        const productImageNodes = (wrapperClassName) => productImagesToRender.map((image, index) => (
        // IMPORTANT: For an uknown reason React treats scene7 URLs as the same key.
        // This is why we needed to add the `index` to the key. See FE-4808.
        // Please, do not remove the index from the key unless there's a better fix for that issue.
        _jsxs("div", Object.assign({ css: titleAndImageStyles(displayedImage === index) }, { children: [_jsxs("p", Object.assign({ css: hoverTitleStyles(wrapperClassName) }, { children: [_jsx("span", Object.assign({ css: headerTextStyles }, { children: image.header })), image.caption && `  ${image.caption}`] })), _jsx("div", Object.assign({ css: imageContainerStyles }, { children: _jsx("img", { alt: image.alt, css: imageStyles(Boolean(shadowOnFirstImage && index === 0)), src: image.url }) }))] }), `${index}-${image.url}`)));
        const badge = (_jsx("img", { alt: "Badge", css: badgeImageStyles, src: badgeUrl }));
        //  SwatchPicker can't have undefined within its children,
        //  thus we need to filter the children array
        const swatchPickerChildren = [
            ...(colorwayColors || []).map(({ colorValue, inputValue, name, }) => (_jsx(Swatch, { color: colorValue, displayName: name, id: `swatch-${inputValue}-${buyUrl}`, value: inputValue }, inputValue))),
            onCustomColorClick && (_jsx(CustomColorSwatch, { displayName: "Custom Color", onClick: onCustomColorClick }, "custom-color")),
        ].filter(Boolean);
        return (_jsx(ClassNames, { children: ({ css: classNameGenerator, cx, }) => {
                const wrapperClassName = cx(classNameGenerator(baseStylesString), className);
                return (_jsxs("div", Object.assign({ className: wrapperClassName }, { children: [_jsx("a", Object.assign({ css: titleAndImageSectionStyles, href: buyUrl, onMouseEnter: cycleOnHover ?
                                () => this.dispatch({
                                    type: actionTypes.startCycling,
                                })
                                : undefined, onMouseLeave: cycleOnHover ?
                                () => this.dispatch({
                                    type: actionTypes.resetCycling,
                                })
                                : undefined }, { children: lazyloadImage ?
                                (_jsx(LazyLoad, Object.assign({ offset: LAZY_LOAD_TOP_OFFSET, once: true }, { children: productImageNodes(wrapperClassName) })))
                                : productImageNodes(wrapperClassName) })), _jsxs("div", Object.assign({ css: quicklookContainerStyles }, { children: [onQuicklook && (_jsx(TextLink, { onClick: onQuicklook, size: "small", text: quicklookLabel })), _jsx(PopoverManager, Object.assign({ position: "top" }, { children: ({ contentRef, targetRef, }) => (_jsxs("div", Object.assign({ css: css `
                        flex: 0 0 auto;
                      `, ref: targetRef }, { children: [!inlineFavoriteButton
                                                && (_jsx(FavoriteIconButton, { favorited: Boolean(favorited), onFavoriteClick: this.handleFavoriteClick, size: "extraSmall" })), _jsx(Popover, Object.assign({ open: favoriteSuggestionOpen, ref: contentRef }, { children: _jsx(PopoverContent, Object.assign({ css: popoverStyles }, { children: _jsxs("button", Object.assign({ css: favoriteSuggestionStyles, onClick: this.handleFavoriteClick, type: "button" }, { children: [_jsx("span", Object.assign({ css: css `
                                color: ${theme.colors.negative800};
                              ` }, { children: "Love It?" })), ' ', "Save it."] })) })) }))] }))) }))] })), _jsxs("div", Object.assign({ css: colorwayWrapperStyles }, { children: [colorwayColors
                                    && colorwayColors.length > 0
                                    && showColorSwatch
                                    && (_jsx("div", Object.assign({ css: swatchPickerStyles }, { children: _jsx(SwatchPicker, Object.assign({ name: `color-picker-${buyUrl}`, onChange: onColorChange, selectionType: "single", size: touchDeviceScreen ? 'xsmall' : 'small', value: selectedColor }, { children: swatchPickerChildren })) }))), inlineFavoriteButton
                                    && (_jsx(FavoriteIconButton, { css: searchFavoriteIconStyles, favorited: Boolean(favorited), onFavoriteClick: this.handleFavoriteClick, size: "extraSmall" }))] })), _jsx("a", Object.assign({ css: productNameStyles, href: buyUrl }, { children: productName })), _jsx("a", Object.assign({ css: artistNameStyles, href: artistUrl }, { children: artistName })), merchandisingLine && (_jsx("p", Object.assign({ css: merchandisingLineStyles }, { children: merchandisingLine }))), badgeUrl && (lazyloadImage ?
                            (_jsx(LazyLoad, Object.assign({ offset: LAZY_LOAD_TOP_OFFSET, once: true }, { children: badge })))
                            : badge), orderSampleText && (_jsx("div", Object.assign({ css: orderSampleStyles }, { children: _jsx(TextLink, { onClick: onOrderSample, size: "small", text: orderSampleText }) }))), !inlineFavoriteButton
                            && (_jsx(FavoriteIconButton, { css: mobileFavoriteButtonStyles, favorited: Boolean(favorited), onFavoriteClick: this.handleFavoriteClick, size: "small" }))] })));
            } }));
    }
}
ProductCellPrimary.defaultProps = defaultProps;
const MemoizedProductCellPrimary = memo(ProductCellPrimary);
export { MemoizedProductCellPrimary as ProductCellPrimary };
// MC-1068 Temporary hack for Storybook.
export default ProductCellPrimary;
